import { useState } from "react";
import { Check, ChevronDown } from "lucide-react";

export const PROPOSAL_TAXONOMY = {
  "Truck Division": ["Sleeper Cab Semi-Trucks", "Day Cab Semi-Trucks", "Heavy-Duty Commercial Trucks", "Medium-Duty Commercial Trucks", "Box Trucks", "Dump Trucks", "Tractor Units", "Specialty Commercial Trucks"],
  "Trailer Division": ["Dry Van Trailers", "Refrigerated Trailers (Reefers)", "Flatbed Trailers", "Step Deck Trailers", "Lowboy / Lowbed Trailers", "Tanker Trailers", "Dump Trailers", "Specialty and Heavy-Haul Trailers"],
  "Marine Division": ["Motor Yachts", "Luxury Yachts", "Motorboats", "Fishing Boats", "Sport Boats", "Cabin Cruisers", "Pontoon Boats", "Commercial Marine Vessels"],
  "Equipment Division": ["Excavators", "Wheel Loaders", "Bulldozers", "Backhoe Loaders", "Skid Steer Loaders", "Telehandlers", "Cranes", "Forklifts", "Industrial Machinery", "Road Construction Equipment"],
};

const slug = (s) => s.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");
export const pickKey = (p) => `${p.division}|${p.type}`;

const Box = ({ checked, partial }) => (
  <span className={`w-4 h-4 grid place-items-center border shrink-0 ${checked || partial ? "border-[var(--gold)] bg-[var(--gold)] text-[#0a0d14]" : "border-white/35"}`}>
    {checked ? <Check className="w-3 h-3" strokeWidth={3} /> : partial ? <span className="w-2 h-0.5 bg-[#0a0d14]" /> : null}
  </span>
);

const Group = ({ division, value, onChange, open, setOpen }) => {
  const types = PROPOSAL_TAXONOMY[division];
  const chosen = types.filter((t) => value.some((p) => p.division === division && p.type === t));
  const all = chosen.length === types.length;
  const toggle = (t) => onChange(chosen.includes(t) ? value.filter((p) => !(p.division === division && p.type === t)) : [...value, { division, type: t }]);
  const toggleAll = () => onChange(all ? value.filter((p) => p.division !== division) : [...value.filter((p) => p.division !== division), ...types.map((t) => ({ division, type: t }))]);
  const id = slug(division);
  return (
    <div className="border hairline bg-[var(--ink)]" data-testid={`asset-group-${id}`}>
      <div className="flex items-center gap-3 p-4">
        <button type="button" role="checkbox" aria-checked={all ? "true" : chosen.length ? "mixed" : "false"} onClick={toggleAll} className="flex items-center gap-3 flex-1 text-left" data-testid={`asset-group-all-${id}`}>
          <Box checked={all} partial={!all && chosen.length > 0} />
          <span className="font-display text-xl text-slate-50">{division}</span>
          {chosen.length > 0 && <span className="text-xs text-gold font-mono">{chosen.length} selected</span>}
        </button>
        <button type="button" onClick={() => setOpen(open ? "" : division)} aria-expanded={open} aria-label={`Show ${division} asset types`} className="p-1.5 text-slate-300 hover:text-gold" data-testid={`asset-group-toggle-${id}`}>
          <ChevronDown className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
      </div>
      {open && (
        <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-1 px-4 pb-4 border-t hairline pt-3">
          {types.map((t) => (
            <li key={t}>
              <button type="button" role="checkbox" aria-checked={chosen.includes(t)} onClick={() => toggle(t)} className="w-full flex items-center gap-3 py-2 text-left text-sm text-slate-200 hover:text-slate-50" data-testid={`asset-pick-${id}-${slug(t)}`}>
                <Box checked={chosen.includes(t)} /> {t}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export const AssetMultiSelect = ({ value, onChange, defaultOpen = "Truck Division", invalid }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <fieldset data-testid="asset-multiselect">
      <legend className="label">Which asset categories does your proposal relate to? *</legend>
      <p className="text-xs text-slate-400 mb-3">Select one or more asset types, across any divisions. Tick a division name to select all of its types.</p>
      <div className="space-y-2">
        {Object.keys(PROPOSAL_TAXONOMY).map((d) => <Group key={d} division={d} value={value} onChange={onChange} open={open === d} setOpen={setOpen} />)}
      </div>
      {invalid && <p className="text-xs text-rose-300 mt-2" data-testid="asset-multiselect-error">Please select at least one asset category.</p>}
      {value.length > 0 && <p className="text-xs text-slate-300 mt-3" data-testid="asset-multiselect-summary">{value.length} asset type{value.length > 1 ? "s" : ""} selected</p>}
    </fieldset>
  );
};
